import {Injectable, OnDestroy} from '@angular/core';
import {Observable, of, Subscription} from 'rxjs';
import {catchError, filter, map, switchMap, timeout} from 'rxjs/operators';
import {v4 as uuid} from 'uuid';
import {clientVersion} from '../../../client-version';
import {AuthService} from '../auth.service';
import {StompConnectionStatus} from './stomp-connection-status.enum';
import {UserAuthorityDaoService} from '../main/user-authority/user-authority-dao.service';
import {DomainPathService} from '../domain-path/domain-path.service';
import {StompService} from '../stomp/stomp.service';
import {FirebaseService} from '../firebase.service';

@Injectable({
    providedIn: 'root',
})
export class R2CloudStompService extends StompService implements OnDestroy {
    private _webClientId!: string;
    private authSubscription!: Subscription;
    private connectionStatusSubscription!: Subscription;

    constructor(private auth: AuthService,
                private userAuthorityDao: UserAuthorityDaoService,
                private domainPathService: DomainPathService,
                private firebaseService: FirebaseService,
    ) {
        super();
        this.authSubscription = this.auth.isLoggedIn().pipe(switchMap((result) => {
            if (result) {
                return this.userAuthorityDao.getMe();
            } else {
                return of(null);
            }
        })).subscribe((result) => {
            if (result) {
                this.connect();
            } else {
                this.send('/stomp/logout', {});
                this._connectionStatus$.next(StompConnectionStatus.DISCONNECTED);
                this._username$.next(null);
                this.disconnect();
            }
        });
        this.connectionStatusSubscription = this.connectionStatus$.pipe(filter(x => x === StompConnectionStatus.DUPLICATE_LOGIN)).subscribe(() => {
            this.auth.duplicatedLogin();
        });
    }

    protected url(): Observable<string> {
        // R2CLOUD-1099: re-sincroniza a sessão HTTP com o token fresco ANTES de cada handshake.
        // O upgrade WS autentica pelo token gravado na sessão; sem isso, após ~1h o token da sessão
        // expira e o backend rejeita o handshake em loop (barreira exp), sumindo de "Usuários online".
        // Falha no sync não impede a tentativa de conexão (catchError).
        return this.firebaseService.refreshSession().pipe(
            timeout(5000),
            catchError(() => of(null)),
            switchMap(() => this.domainPathService.apiWebSocketURL$),
            map((apiWebSocketURL) =>
                `${apiWebSocketURL}stomp/web?X-Client-Id=${this.webClientId}` +
                `&X-Client-Version=${clientVersion}`)
        );
    }

    get webClientId(): string {
        if (!this._webClientId) {
            let item: string | null = localStorage.getItem('_webClientId');
            if (!item) {
                item = uuid().replace(/-/g, '');
                localStorage.setItem('_webClientId', item);
            }
            this._webClientId = item;
        }
        return this._webClientId;
    }

    override ngOnDestroy(): void {
        this.authSubscription?.unsubscribe();
        this.connectionStatusSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
